import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Breadcrumb, Button, Card, Col, Form, ListGroup, Row
} from "react-bootstrap";
import { useStateMachine } from "little-state-machine";
import {
  Divider, NoData, Spinner, Stepper
} from "@whitespace/whitespace-ui";
import { Link, useNavigate } from "react-router-dom";
import { useAmplifyREST } from "hooks/useAmplifyREST";
import { assignmentStepsWhitespace } from "modules/assignmentSteps";
import { useForm } from "react-hook-form";
import IRoleWhitespace from "interfaces/models/IRoleWhitespace";
import IAssignWhitespacePermissionsRequest from "interfaces/requests/IAssignWhitespacePermissionsRequest";
import { updateAssignWhitespacePermissionsRequest } from "actions/updateAssignWhitespacePermissionsRequest";
import { FlowOrigination } from "interfaces/FlowOrigination";
import { ApplicationType } from "interfaces/models/ApplicationType";
import IClaimWhitespace from "interfaces/responses/IClaimWhitespace";
import { mapWhitespaceClaimToRole } from "mappers/mapWhitespaceClaimToRole/mapWhitespaceClaimToRole";
import { ClaimType } from "interfaces/models/ClaimType";
import { updateAssignedClaimsWhitespace } from "actions/updateAssignedClaimsWhitespace";
import AmplifyError from "components/AmplifyError/AmplifyError";
import { initialAssignWhitespacePermissionsRequest } from "modules/initialAssignWhitespacePermissionsRequest";
import { updateCurrentWhitespaceClaimsRoles } from "actions/updateCurrentWhitespaceClaimsRoles";
import UserRoleItem from "./UserRoleItem/UserRoleItem";

interface AssignUserWhitespaceRolesProps {
  flowOrigination : FlowOrigination
}

const AssignUserWhitespaceRoles = ({ flowOrigination } : AssignUserWhitespaceRolesProps) => {
  const navigate = useNavigate();

  const { actions } = useStateMachine({
    updateAssignWhitespacePermissionsRequest,
    updateAssignedClaimsWhitespace,
    updateCurrentWhitespaceClaimsRoles
  });

  const {
    isSending: rolesIsSending, errors: rolesError, data: rolesData, sendRequest: rolesSendRequest
  } = useAmplifyREST();

  const {
    isSending: assignedClaimsIsSending, errors: assignedClaimsError, data: assignedClaimsData, sendRequest: assignedClaimsSendRequest
  } = useAmplifyREST();

  const { state } = useStateMachine();
  const [anyRolesSelected, setAnyRolesSelected] = useState(false);
  const [roles, setRoles] = useState<IRoleWhitespace[] | undefined>(undefined);
  const [claimsAssigned, setClaimsAssigned] = useState<IClaimWhitespace[] | undefined>(undefined);
  const stepperItems : string[] = assignmentStepsWhitespace;

  const {
    register, handleSubmit, getValues, setValue, watch
  } = useForm({
    mode: "onSubmit"
  });

  const apiErrors = rolesError || assignedClaimsError;

  const watchFields = watch(["roles"]);

  useEffect(() => {
    if (!state.assignWhitespacePermissionsRequest && claimsAssigned) {
      const request = initialAssignWhitespacePermissionsRequest();
      request.userId = state.selectedUser!.userId;
      request.applicationId = state.selectedUserApplicationDetails!.applicationDetails.applicationId;
      request.authorityIds = claimsAssigned
        .filter((claim : IClaimWhitespace) => claim.type === ClaimType.Authority).map((claim : IClaimWhitespace) => claim.id);
      request.roleIds = claimsAssigned.filter((claim : IClaimWhitespace) => claim.type === ClaimType.Role).map((claim : IClaimWhitespace) => claim.id);

      actions.updateAssignWhitespacePermissionsRequest(request);
    }
  }, [state.assignWhitespacePermissionsRequest, claimsAssigned]);

  useEffect(() => {
    const rolesSelected = getValues("roles") as number[];
    const areRolesSelected = rolesSelected && rolesSelected.length > 0;
    setAnyRolesSelected(areRolesSelected);
  }, [watchFields]);

  useEffect(() => {
    // eslint-disable-next-line max-len
    const url = `apps/${encodeURIComponent(state.selectedUserApplicationDetails!.applicationDetails.applicationId)}/claim_types/roles/claims`;
    rolesSendRequest(url, "GET", null);
  }, [rolesSendRequest]);

  useEffect(() => {
    if (rolesData) {
      actions.updateCurrentWhitespaceClaimsRoles(rolesData as IClaimWhitespace[]);
      setRoles(
        rolesData.map(
          (claim : IClaimWhitespace) => mapWhitespaceClaimToRole(claim)
        )
      );
    }
  }, [rolesData]);

  useEffect(() => {
    // eslint-disable-next-line max-len
    const url = `apps/${encodeURIComponent(state.selectedUserApplicationDetails!.applicationDetails.applicationId)}/users/${encodeURIComponent(state.selectedUser!.userId)}/claims`;
    assignedClaimsSendRequest(url, "GET", null);
  }, [assignedClaimsSendRequest]);

  useEffect(() => {
    if (assignedClaimsData) {
      actions.updateAssignedClaimsWhitespace(assignedClaimsData as IClaimWhitespace[]);

      setClaimsAssigned(assignedClaimsData as IClaimWhitespace[]);
    }
  }, [assignedClaimsData]);

  const renderRoles = () => roles?.map((role : IRoleWhitespace, idx : number) => (
    <UserRoleItem register={register} key={`userRoleItem_${idx}`} role={role} />
  ));

  const constructRequestFromForm = useCallback(() => {
    if (state.assignWhitespacePermissionsRequest) {
      const request : IAssignWhitespacePermissionsRequest = initialAssignWhitespacePermissionsRequest();
      request.applicationId = state.assignWhitespacePermissionsRequest!.applicationId;
      request.userId = state.assignWhitespacePermissionsRequest!.userId;
      request.authorityIds = state.assignWhitespacePermissionsRequest!.authorityIds;

      const rolesSelected = getValues("roles") as number[];
      if (rolesSelected) {
        if (typeof rolesSelected === "string") {
          request!.roleIds.push(rolesSelected);
        } else {
          rolesSelected.forEach((roleId : number) => {
            request!.roleIds.push(+roleId);
          });
        }
      }

      actions.updateAssignWhitespacePermissionsRequest(request);
    }
  }, [getValues, state.assignWhitespacePermissionsRequest]);

  const setControlValues = useCallback(() => {
    if (state.assignWhitespacePermissionsRequest) {
      setValue("roles", state.assignWhitespacePermissionsRequest.roleIds.map(String));
    }
  }, [setValue, state.assignWhitespacePermissionsRequest]);

  useEffect(() => {
    setControlValues();
  }, [setControlValues]);

  const handleSelectClick = () => {
    constructRequestFromForm();

    if (flowOrigination === FlowOrigination.UserManagement) {
      if (state.selectedUserApplicationDetails!.applicationDetails.applicationType === ApplicationType.Whitespace) {
        navigate("/assignuserwhitespaceauthorities");
      } else {
        navigate("/assignuserwhitespaceregions");
      }
    } else if (state.selectedUserApplicationDetails!.applicationDetails.applicationType === ApplicationType.Whitespace) {
      navigate("/requestsassignuserwhitespaceauthorities");
    } else {
      navigate("/requestsassignuserwhitespaceregions");
    }
  };

  const handleBackButtonClick = () => {
    if (flowOrigination === FlowOrigination.UserManagement) {
      navigate("/manageuserapplications");
    } else {
      navigate("/requests");
    }
  };

  return (
    <>
      {apiErrors && <AmplifyError errors={apiErrors} />}
      <Card>
        <Card.Header>
          <Row className="w-100">
            <Col xs={8}>
              <h1>
                Assign Roles
                <Spinner animation="grow" visible={rolesIsSending || assignedClaimsIsSending} spinnerText="Loading" variant="info" />
              </h1>
            </Col>
            <Col xs={4} className="d-flex justify-content-end">
              <span className="selected-user-title">{state.selectedUser?.username}</span>
            </Col>
          </Row>
        </Card.Header>
        <Card.Body className="no-padding">
          <Row className="d-flex w-100 align-items-center top-margin-small bottom-margin-small">
            <Col xs={8} className="d-flex">
              <Breadcrumb className="breadcrumb-manage-user left-margin-large no-wrap">
                {flowOrigination === FlowOrigination.Requests
            && (
              <>
                <Breadcrumb.Item
                  data-testid="breadcrumb-requests"
                  linkAs={Link}
                  linkProps={{ to: "/requests" }}
                  onClick={() => navigate("/requests")}
                >
                  Requests
                </Breadcrumb.Item>
                <Breadcrumb.Item active>
                  {state.selectedUserApplicationDetails!.applicationDetails.siteUrl}
                </Breadcrumb.Item>
                <Breadcrumb.Item active>
                  Roles
                </Breadcrumb.Item>

              </>
            )}
                {flowOrigination === FlowOrigination.UserManagement
            && (
              <>
                <Breadcrumb.Item
                  data-testid="breadcrumb-users"
                  linkAs={Link}
                  linkProps={{ to: "/users" }}
                  onClick={() => navigate("/users")}
                >
                  Users
                </Breadcrumb.Item>

                <Breadcrumb.Item
                  data-testid="breadcrumb-manage-users"
                  linkAs={Link}
                  linkProps={{ to: "/manageuser" }}
                  onClick={() => navigate("/manageuser")}
                >
                  Manage User
                </Breadcrumb.Item>
                <Breadcrumb.Item
                  data-testid="breadcrumb-manage-user-applications"
                  linkAs={Link}
                  linkProps={{ to: "/manageuserapplications" }}
                  onClick={() => navigate("/manageuserapplications")}
                >
                  Applications
                </Breadcrumb.Item>
                <Breadcrumb.Item active>
                  {state.selectedUserApplicationDetails!.applicationDetails.siteUrl}
                </Breadcrumb.Item>
                <Breadcrumb.Item active>
                  Roles
                </Breadcrumb.Item>

              </>
            )}
              </Breadcrumb>
            </Col>
          </Row>
          <Divider />
          <Form noValidate onSubmit={handleSubmit(handleSelectClick)}>
            <Row className="d-flex w-100 align-items-center top-margin-small bottom-margin-small">
              <Col xs={8} className="d-flex" data-testid="stepper-container">
                <Stepper className="w-100 ms-4 mb-0" steps={stepperItems} currentStep={0} />
              </Col>
              <Col xs={4} className="d-flex justify-content-end">
                <Button variant="secondary" className="me-2" onClick={() => handleBackButtonClick()}>
                  Back
                </Button>
                <Button variant="primary" type="submit" disabled={!anyRolesSelected}>
                  Select
                </Button>
              </Col>
            </Row>
            <Divider />
            {roles && claimsAssigned && (
              <ListGroup variant="flush" className="striped highlight">
                <NoData visible={roles.length === 0} description="No Roles Found" />
                {renderRoles()}
              </ListGroup>
            )}
          </Form>
        </Card.Body>
      </Card>
    </>
  );
};

export default AssignUserWhitespaceRoles;
